import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { fadeUp, fadeUpReduced, viewportOnce } from '../lib/motion';
import { withCharacterName } from '../lib/characters';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import { CUSTOM_ORDERS_ENABLED, DEFAULT_CUSTOM_PRICE } from '../lib/shop';
import {productMedia, productVerso} from '../utils/productMedia';
import {ProductVisual} from './ProductMedia';
import '../pages/ShopAtelier.css';

const API = (process.env.REACT_APP_BACKEND_URL || '');

const COPY = {
  fr: {
    added: name => `${name} vous attend dans le placard`, onlyOne: 'Il n’en existe qu’une.',
    loginForWishlist: 'Connectez-vous pour gérer votre wishlist', wishlistError: 'La wishlist n’a pas été mise à jour',
    adopted: 'Déjà adoptée', toOrder: 'Sur commande', available: 'Pièce disponible', unavailable: 'Indisponible',
    removeWishlist: name => `Retirer ${name} de la wishlist`, addWishlist: name => `Ajouter ${name} à la wishlist`,
    customFrom: price => `À composer · ${price}`, discover: 'Découvrir', seeProduct: 'Voir la fiche',
    adoptedBadge: 'Adoptée', customPiece: price => `la pièce cousue sur mesure : ${price}`, uniquePiece: 'pièce unique',
    designMine: 'Composer la mienne', adopt: 'Adopter', back: 'Dos',
  },
  en: {
    added: name => `${name} is waiting for you in the cupboard`, onlyOne: 'There is only one.',
    loginForWishlist: 'Log in to manage your wishlist', wishlistError: 'Your wishlist couldn’t be updated',
    adopted: 'Already adopted', toOrder: 'Made to order', available: 'Available', unavailable: 'Unavailable',
    removeWishlist: name => `Remove ${name} from your wishlist`, addWishlist: name => `Add ${name} to your wishlist`,
    customFrom: price => `Made to order · ${price}`, discover: 'Discover', seeProduct: 'View details',
    adoptedBadge: 'Adopted', customPiece: price => `made-to-order piece: ${price}`, uniquePiece: 'one of a kind',
    designMine: 'Design mine', adopt: 'Adopt', back: 'Back',
  },
};

const apiSrc = raw => (raw && raw.startsWith('/api/') ? `${API}${raw}` : raw);

/**
 * Recto / verso : le dos en Liberty remplace la face au survol et reste visible
 * en vignette, pour distinguer d'un coup d'œil les sœurs d'un même modèle.
 */
function VersoPeek({ verso, label }) {
  const src = apiSrc(verso.src);
  return (
    <>
      <img className="piece-verso" src={src} alt="" aria-hidden="true" loading="lazy" decoding="async" />
      <span className="piece-verso-chip" aria-hidden="true">
        <img src={src} alt="" loading="lazy" decoding="async" />{label}
      </span>
    </>
  );
}

/**
 * Planche d'atelier : les photos sont détourées (fond transparent), la pièce
 * entière tient dans le cadre et repose sur le fond teinté de sa couleur.
 */
function PiecePlate({ product, verso, backLabel }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const src = apiSrc(product.images?.[0]);
  const tint = product.placeholder_color || '#F6E3A0';

  return (
    <div
      className={`piece-plate aspect-[4/5] overflow-hidden rounded-[1.15rem] border border-border shadow-soft transition-shadow duration-500 ease-soft group-hover:shadow-ambient${verso ? ' has-verso' : ''}`}
      style={{ '--piece-tint': `${tint}80` }}
    >
      {src && !failed ? (
        <img
          src={src}
          alt={product.name}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`piece-recto absolute inset-0 h-full w-full object-contain object-center p-[6%] transition-[opacity,transform] duration-700 ease-soft motion-safe:group-hover:scale-[1.04] ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="h-20 w-20 rounded-full opacity-70"
            style={{ background: `radial-gradient(circle at 34% 28%, hsl(0 0% 100% / 0.85), ${tint})` }}
            aria-hidden="true"
          />
        </div>
      )}
      {verso && src && !failed && <VersoPeek verso={verso} label={backLabel} />}
    </div>
  );
}

/**
 * Card produit — direction « planche d'atelier ».
 * Volontairement sans n° de registre, sans tagline et sans badge « Pièce
 * unique » : elles le sont toutes, donc l'écrire partout ne dit plus rien.
 */
export default function ProductCardV2({ product: rawProduct, showWishlistBtn = true, catalogue = false }) {
  const c = useCopy(COPY);
  const { locale, lp, price } = useLocale();
  const product = withCharacterName(rawProduct, locale);
  const verso = productVerso(product, { locale });
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user, token, refreshUser } = useAuth();
  const reduceMotion = useReducedMotion();

  const isAdopted = !product.made_to_order && (product.status === 'adopted' || product.stock === 0);
  // Sur mesure disponible sauf si explicitement désactivé côté admin
  const canCustom = CUSTOM_ORDERS_ENABLED && product.custom_available !== false;
  const isInWishlist = user?.wishlist?.includes(product.slug);

  const handleAddToCart = () => {
    addItem(product, product.colors?.[0]?.name || '');
    toast.success(c.added(product.name), { description: c.onlyOne });
  };

  const handleCustomOrder = () => navigate(`/sur-mesure?modele=${product.slug}`);

  const handleWishlist = async () => {
    if (!user) { toast.error(c.loginForWishlist); return; }
    try {
      await fetch(`${API}/api/user/wishlist/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_slug: product.slug })
      });
      await refreshUser();
    } catch { toast.error(c.wishlistError); }
  };

  if (catalogue) {
    // Format français historique de la carte (64,5 €) ; en anglais, €64.50.
    const formatPrice = value => (!Number.isFinite(value) ? null : locale === 'en' ? price(value)
      : `${value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`);
    const customPrice = product.custom_price ?? DEFAULT_CUSTOM_PRICE;
    const availabilityLabel = product.status === 'adopted' ? c.adopted : product.made_to_order
      ? c.toOrder : product.stock > 0 ? c.available : c.unavailable;
    const availabilityTint = product.status === 'adopted' ? 'pastel-chip pastel--lilas'
      : !product.made_to_order && product.stock > 0 ? 'pastel-chip pastel--menthe' : '';

    return (
      <motion.article
        variants={reduceMotion ? fadeUpReduced : fadeUp}
        initial="hidden" whileInView="show" viewport={viewportOnce}
        className="group atelier-catalogue-card"
      >
        <div className={`atelier-catalogue-media${verso ? ' has-verso' : ''}`}>
          <ProductVisual media={productMedia(product, { transparent: true, locale })[0]} thumbnail />
          {verso && <VersoPeek verso={verso} label={c.back} />}
        </div>
        {showWishlistBtn && <button type="button" onClick={handleWishlist}
          className="atelier-catalogue-wishlist"
          aria-label={isInWishlist ? c.removeWishlist(product.name) : c.addWishlist(product.name)}>
          <Heart size={17} strokeWidth={1.75} className={isInWishlist ? 'fill-current' : ''} />
        </button>}
        <div className="atelier-catalogue-body">
          <h3><Link to={lp(`/produit/${product.slug}`)} data-testid={`product-card-${product.slug}`}
            className="stretched-link">{product.name}</Link></h3>
          <div className="atelier-catalogue-price-row">
            <p className="atelier-catalogue-price">{formatPrice(product.price)}</p>
            {availabilityLabel && <span className={`atelier-catalogue-stock ${availabilityTint}`}>{availabilityLabel}</span>}
          </div>
          {canCustom && <p className="atelier-catalogue-custom">{c.customFrom(formatPrice(customPrice))}</p>}
          <Link to={lp(`/produit/${product.slug}`)} className="atelier-catalogue-cta">
            <span className="atelier-catalogue-cta-desktop">{c.discover}</span>
            <span className="atelier-catalogue-cta-mobile">{c.seeProduct}</span>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </div>
      </motion.article>
    );
  }

  return (
    <motion.article
      variants={reduceMotion ? fadeUpReduced : fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={viewportOnce}
      className="group relative flex h-full flex-col"
    >
      <PiecePlate product={product} verso={verso} backLabel={c.back} />

      {isAdopted && (
        <span className="absolute left-3 top-3 z-[2] inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--card))] px-3 py-1 font-sans text-[11px] font-medium text-foreground shadow-soft">
          <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-rose-dust))]" aria-hidden="true" />
          {c.adoptedBadge}
        </span>
      )}

      {showWishlistBtn && (
        <button
          type="button"
          onClick={handleWishlist}
          className="absolute right-3 top-3 z-[2] flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--card))] shadow-soft transition-[opacity,transform] duration-200 hover:scale-105 motion-reduce:hover:scale-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus-visible:opacity-100"
          aria-label={isInWishlist ? c.removeWishlist(product.name) : c.addWishlist(product.name)}
        >
          <Heart
            className={`h-4 w-4 ${isInWishlist ? 'fill-primary text-primary' : 'text-foreground'}`}
            strokeWidth={1.75}
          />
        </button>
      )}

      <div className="flex flex-1 flex-col px-0.5 pt-4">
        <h3 className="font-serif text-[17px] leading-tight text-foreground">
          <Link
            to={lp(`/produit/${product.slug}`)}
            data-testid={`product-card-${product.slug}`}
            className="stretched-link rounded-sm"
          >
            {product.name}
          </Link>
        </h3>


        <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
          <p className="font-sans text-sm font-semibold tabular-nums text-foreground">
            {price(product.price)}
          </p>
          <p className="font-sans text-[11.5px] text-[hsl(var(--muted-foreground))]">
            {isAdopted
              ? c.customPiece(price(product.custom_price ?? DEFAULT_CUSTOM_PRICE))
              : c.uniquePiece}
          </p>
        </div>

        <div className="mt-auto pt-4">
          <div className="h-px w-full bg-[hsl(var(--brand-gold)/0.3)]" />
          {isAdopted ? (
            canCustom ? (
              <button
                type="button"
                onClick={handleCustomOrder}
                className="relative z-[2] mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-[hsl(var(--brand-gold)/0.75)] py-2.5 font-sans text-[13px] font-medium text-foreground transition-colors duration-200 hover:border-solid hover:border-[hsl(var(--brand-gold))] hover:bg-[hsl(var(--brand-gold))] hover:text-[hsl(var(--brand-porcelain))]"
              >
                {c.designMine}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : null
          ) : (
            <button
              type="button"
              onClick={handleAddToCart}
              data-testid={`product-add-to-cart-${product.slug}`}
              className="relative z-[2] mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[hsl(var(--brand-gold)/0.75)] py-2.5 font-sans text-[13px] font-medium text-foreground transition-colors duration-200 hover:border-primary hover:bg-primary hover:text-primary-foreground"
            >
              {c.adopt}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
}
